import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCenter,
} from "@dnd-kit/core";
import {
  Loader2, RefreshCw, Calendar as CalendarIcon, Bus, AlertTriangle,
  ChevronLeft, ChevronRight, X, MapPin, Users, Clock, Wifi, WifiOff,
  Search, UserCog, Layers, Sparkles, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/lib/realtime";

// ─── Constants ──────────────────────────────────────────────────────
const HOUR_START = 4;
const HOUR_END = 22;
const PIXELS_PER_MIN = 1.2;          // 1.2px/min → 1296px wide
const ROW_HEIGHT = 52;
const SIDEBAR_W = 230;
const POOL_W = 280;

const totalWidth = (HOUR_END - HOUR_START) * 60 * PIXELS_PER_MIN;

// ─── Helpers ────────────────────────────────────────────────────────
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function addDaysIso(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function frenchDow(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}
function clampMinutes(m) {
  return Math.max(HOUR_START * 60, Math.min(HOUR_END * 60, m));
}
function pxFromMin(m) {
  return (clampMinutes(m) - HOUR_START * 60) * PIXELS_PER_MIN;
}

const COLOR_BG = {
  green:  "bg-emerald-500/90 border-emerald-700 text-white",
  orange: "bg-amber-500/95 border-amber-700 text-zinc-900",
  red:    "bg-red-500/95 border-red-700 text-white",
  blue:   "bg-sky-500/90 border-sky-700 text-white",
  gray:   "bg-zinc-300 border-zinc-500 text-zinc-800",
};

// ─── Time ruler (hour bands) ───────────────────────────────────────
function TimeRuler() {
  const hours = [];
  for (let h = HOUR_START; h <= HOUR_END; h++) hours.push(h);
  return (
    <div
      className="sticky top-0 z-20 bg-zinc-900 border-b-2 border-[#FFC60A] h-10"
      style={{ width: totalWidth }}
      data-testid="dispatch-time-ruler"
    >
      <div className="relative h-full">
        {hours.map((h) => (
          <div
            key={h}
            className="absolute top-0 bottom-0 border-l border-zinc-700 text-[10px] font-mono text-zinc-300 px-1.5 pt-1.5"
            style={{ left: (h - HOUR_START) * 60 * PIXELS_PER_MIN }}
          >
            {String(h).padStart(2, "0")}:00
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── A draggable timeline segment ──────────────────────────────────
function DraggableSegment({ seg, vehicleId, onSelect, selected }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `seg:${seg.id}`,
    data: { type: "segment", segId: seg.id, fromVehicleId: vehicleId, code: seg.code },
  });
  const left = pxFromMin(seg.start_minutes);
  const width = Math.max(40, pxFromMin(seg.end_minutes) - left);
  const cls = COLOR_BG[seg.color] || COLOR_BG.gray;
  const style = {
    left,
    width,
    top: 6,
    height: ROW_HEIGHT - 12,
    opacity: isDragging ? 0.4 : 1,
  };
  const hasExceptionLoc = !!(seg.exception_poi_name || seg.exception_address || seg.exception_operational_notes);
  const exceptionTooltip = hasExceptionLoc
    ? `📍 EXCEPTION — ${seg.exception_poi_name || seg.exception_address || ""}`
      + (seg.exception_start_datetime && !seg.exception_all_day
        ? ` (${String(seg.exception_start_datetime).slice(11, 16)}–${String(seg.exception_end_datetime || "").slice(11, 16)})`
        : seg.exception_all_day ? " (toute la journée)" : "")
      + (seg.exception_operational_notes ? `\n${seg.exception_operational_notes}` : "")
    : "";
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      type="button"
      onClick={(e) => { e.stopPropagation(); onSelect?.(seg); }}
      className={`absolute rounded-md border-l-4 shadow-sm hover:shadow-md transition-all px-2 text-left ${cls} ${selected ? "ring-2 ring-[#FFC60A] ring-offset-1 ring-offset-zinc-50" : ""}`}
      style={style}
      data-testid={`dispatch-segment-${seg.id}`}
      title={`${seg.code} ${seg.start_time}–${seg.end_time} • ${seg.client_name || ""}${exceptionTooltip ? "\n\n" + exceptionTooltip : ""}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-bold leading-tight truncate">
        {seg.has_conflict && <AlertTriangle size={11} className="shrink-0" />}
        {hasExceptionLoc && (
          <MapPin
            size={11}
            className="shrink-0 text-purple-700"
            data-testid={`dispatch-segment-exception-${seg.id}`}
          />
        )}
        <span className="truncate">{seg.code}</span>
        <span className="opacity-80 font-mono text-[10px] ml-auto">{seg.start_time}</span>
      </div>
      <div className="text-[10px] opacity-90 truncate">{seg.client_name || "—"}</div>
    </button>
  );
}

// ─── A drop-target row for a vehicle (timeline lane) ───────────────
function VehicleLane({ vehicle, onSelect, selectedSegId }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `veh:${vehicle.id}`,
    data: { type: "vehicle", vehicleId: vehicle.id },
  });
  return (
    <div
      ref={setNodeRef}
      className={`relative border-b border-zinc-200 ${isOver ? "bg-[#FFC60A]/20" : "bg-white"}`}
      style={{ height: ROW_HEIGHT, width: totalWidth }}
      data-testid={`dispatch-lane-${vehicle.plate || vehicle.id}`}
    >
      {/* Hour grid lines */}
      {Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => (
        <div
          key={i}
          className="absolute top-0 bottom-0 border-l border-zinc-100 pointer-events-none"
          style={{ left: i * 60 * PIXELS_PER_MIN }}
        />
      ))}
      {(vehicle.segments || []).map((seg) => (
        <DraggableSegment
          key={seg.id}
          seg={seg}
          vehicleId={vehicle.id}
          onSelect={onSelect}
          selected={selectedSegId === seg.id}
        />
      ))}
    </div>
  );
}

// ─── Sticky left column row for a bus ──────────────────────────────
const VEHICLE_STATUS_FR = {
  active: "Actif",
  maintenance: "Maintenance",
  out_of_service: "Hors service",
  inactive: "Inactif",
};
function VehicleCell({ vehicle }) {
  const shortCode = vehicle.plate || vehicle.label || "—";
  // Full bus name: prefer model (e.g. "ARB Chat - 32"), then label, then plate as last resort.
  const fullName = vehicle.model || vehicle.label || vehicle.plate || "—";
  const status = vehicle.status || "active";
  const Wi = vehicle.gps_ok ? Wifi : WifiOff;
  return (
    <div
      className="flex items-center gap-2 px-3 border-b border-zinc-200 bg-zinc-50"
      style={{ height: ROW_HEIGHT }}
      data-testid={`dispatch-bus-${shortCode}`}
      title={fullName}
    >
      <div className="w-8 h-8 bg-zinc-900 text-[#FFC60A] flex items-center justify-center font-display font-black text-xs shadow-[2px_2px_0_0_#FFC60A] flex-shrink-0">
        {String(shortCode).slice(0, 3)}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="text-xs font-bold text-zinc-900 truncate leading-tight"
          data-testid={`dispatch-bus-name-${shortCode}`}
        >
          {fullName}
        </div>
        <div className="text-[10px] text-zinc-500 truncate flex items-center gap-1">
          <Wi size={9} className={vehicle.gps_ok ? "text-emerald-600" : "text-zinc-400"} />
          {vehicle.capacity ? `${vehicle.capacity} pl.` : "—"}
          {status !== "active" && <span className="text-red-600 font-bold ml-1 uppercase">{VEHICLE_STATUS_FR[status] || status}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Unassigned pool (right-side draggable cards) ──────────────────
function PoolItem({ seg, onSelect, selected }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pool:${seg.id}`,
    data: { type: "pool", segId: seg.id, code: seg.code },
  });
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      type="button"
      onClick={(e) => { e.stopPropagation(); onSelect?.(seg); }}
      className={`w-full text-left bg-white border-2 border-zinc-300 hover:border-[#FFC60A] rounded-lg p-2.5 transition shadow-sm ${isDragging ? "opacity-40" : ""} ${selected ? "ring-2 ring-[#FFC60A]" : ""}`}
      data-testid={`dispatch-pool-${seg.id}`}
    >
      <div className="flex items-center gap-2">
        <Layers size={13} className="text-zinc-500" />
        <span className="text-xs font-bold text-zinc-900">{seg.code}</span>
        <span className="ml-auto font-mono text-[10px] text-zinc-600">{seg.start_time}–{seg.end_time}</span>
      </div>
      <div className="text-[11px] text-zinc-600 truncate mt-0.5">{seg.client_name || "—"}</div>
      <div className="text-[10px] text-zinc-400 mt-0.5">{seg.stops_count} arrêts</div>
    </button>
  );
}

// ─── KPI tiles ─────────────────────────────────────────────────────
function Kpi({ label, value, tone = "default", testId }) {
  const tones = {
    default: "bg-zinc-900 text-white",
    ok:      "bg-emerald-600 text-white",
    warn:    "bg-amber-500 text-zinc-900",
    alert:   "bg-red-600 text-white",
    pool:    "bg-sky-600 text-white",
  };
  return (
    <div className={`flex flex-col px-3 py-2 min-w-[88px] shadow-[3px_3px_0_0_#0A0A0B] border border-zinc-900 ${tones[tone] || tones.default}`} data-testid={testId}>
      <span className="text-[9px] font-mono uppercase tracking-[0.2em] opacity-80">{label}</span>
      <span className="text-2xl font-display font-black leading-none mt-0.5">{value}</span>
    </div>
  );
}

// ─── Source badge (Excel / GTFS / Manuel) ──────────────────────────
function detectSource(ps) {
  if (!ps) return { id: "unknown", label: "—", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" };
  if (ps.gtfs_trip_id) return { id: "gtfs", label: "GTFS", cls: "bg-sky-100 text-sky-800 border-sky-300" };
  if (ps.template_id)  return { id: "excel", label: "EXCEL", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  return { id: "manual", label: "MANUEL", cls: "bg-amber-100 text-amber-800 border-amber-300" };
}

function SourceBadge({ ps }) {
  const s = detectSource(ps);
  return (
    <span data-testid={`dispatch-src-${s.id}`}
      className={`inline-flex items-center text-[9px] font-mono font-bold uppercase tracking-widest border px-1.5 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
}

// ─── Right slide-over panel for the selected service ───────────────
function ServiceDetailPanel({ ps, onClose }) {
  if (!ps) return null;
  const stops = ps.stops || [];
  const alerts = ps.alerts || [];
  return (
    <div className="fixed right-0 top-0 bottom-0 w-[420px] bg-white border-l-2 border-zinc-900 shadow-2xl z-50 overflow-y-auto" data-testid="dispatch-detail-panel">
      <div className="sticky top-0 bg-zinc-900 text-white px-4 py-3 flex items-center gap-2 border-b-2 border-[#FFC60A]">
        <Bus size={16} className="text-[#FFC60A]" />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
            Service
            <SourceBadge ps={ps} />
          </div>
          <div className="font-display font-black text-lg truncate">
            {ps.service_code}{ps.service_letter || ""} — {ps.client_name || "—"}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-zinc-700 rounded"
          data-testid="dispatch-detail-close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Times */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-100 p-3 border-l-4 border-zinc-900">
            <div className="text-[10px] font-mono uppercase text-zinc-500">Début</div>
            <div className="font-mono font-bold text-lg">{ps.start_time || "—"}</div>
          </div>
          <div className="bg-zinc-100 p-3 border-l-4 border-zinc-900">
            <div className="text-[10px] font-mono uppercase text-zinc-500">Fin</div>
            <div className="font-mono font-bold text-lg">{ps.end_time || "—"}</div>
          </div>
        </div>

        {/* Vehicle */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Bus affecté</div>
          {ps.effective_vehicle_id ? (
            <div className="bg-emerald-50 border border-emerald-300 p-2 text-sm">
              <span className="font-bold">{ps.effective_vehicle_label || ps.effective_vehicle_plate}</span>
              {ps.effective_vehicle_capacity ? <span className="ml-2 text-zinc-600">{ps.effective_vehicle_capacity} pl.</span> : null}
              <span className="ml-2 text-[10px] uppercase font-mono text-zinc-500">{ps.assignment_kind}</span>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-300 p-2 text-sm flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-700" /> Aucun bus affecté
            </div>
          )}
        </div>

        {/* Driver — READ-ONLY (Planning Bus = visualisation uniquement) */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Chauffeur principal</div>
          {ps.effective_driver_name ? (
            <div className="bg-zinc-50 border border-zinc-300 p-2 text-sm flex items-center gap-2"
                  data-testid="dispatch-detail-driver-readonly">
              <UserCog size={14} className="text-zinc-600" />
              <span className="font-bold">{ps.effective_driver_name}</span>
            </div>
          ) : (
            <div className="bg-zinc-50 border border-zinc-200 p-2 text-xs text-zinc-500 italic"
                  data-testid="dispatch-detail-no-driver">
              Aucun chauffeur principal défini sur la fiche du véhicule.
            </div>
          )}
        </div>

        {/* Configuration shortcut — single CTA, replaces the old change/unassign buttons */}
        <div>
          {ps.effective_vehicle_id ? (
            <a
              href={`/admin/vehicles?focus_vehicle=${ps.effective_vehicle_id}`}
              data-testid="dispatch-detail-open-config"
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-900 text-[#FFC60A] border border-[#FFC60A] font-bold text-xs uppercase tracking-wider hover:bg-[#FFC60A] hover:text-zinc-900">
              <UserCog size={13} /> Ouvrir la configuration du véhicule ↗
            </a>
          ) : (
            <>
              <div className="text-xs text-zinc-500 italic mb-1.5">
                Aucune configuration permanente trouvée.
              </div>
              <a
                href="/admin/vehicles"
                data-testid="dispatch-detail-open-vehicles"
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-900 text-[#FFC60A] border border-[#FFC60A] font-bold text-xs uppercase tracking-wider hover:bg-[#FFC60A] hover:text-zinc-900">
                <UserCog size={13} /> Ouvrir la configuration ↗
              </a>
            </>
          )}
          {ps.assignment_kind === "override" && (
            <div className="text-[10px] text-purple-700 mt-1.5 font-mono">
              ⓘ Une exception est active aujourd&apos;hui — gérez-la dans
              <a href="/admin/substitutions" className="underline font-bold ml-1">Exceptions</a>.
            </div>
          )}
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="border-2 border-red-300 bg-red-50 p-2">
            <div className="text-[10px] font-mono uppercase text-red-700 font-bold mb-1">Alertes</div>
            <ul className="text-xs space-y-1">
              {alerts.map((a, i) => (
                <li key={i} className="flex gap-1.5"><AlertTriangle size={11} className="text-red-700 mt-0.5" />{a.label || a.code}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Stops */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-1">
            <MapPin size={11} /> Arrêts ({stops.length})
          </div>
          <ol className="border border-zinc-200 divide-y divide-zinc-100">
            {stops.map((s, i) => (
              <li key={i} className="px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-zinc-50">
                <span className="font-mono text-[10px] text-zinc-400 w-5">{s.sequence || i + 1}</span>
                <span className="font-mono text-[10px] font-bold text-zinc-700 w-12">{s.arrival || s.departure || "—"}</span>
                <span className="flex-1 truncate">{s.name}</span>
                {s.boarding ? <span className="text-emerald-700 font-bold text-[10px]">+{s.boarding}</span> : null}
                {s.alighting ? <span className="text-red-700 font-bold text-[10px]">-{s.alighting}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────
export default function Dispatch() {
  const { user } = useAuth();
  const [dateIso, setDateIso] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all|busy|free|conflict|no_gps|no_driver
  const [sourceFilter, setSourceFilter] = useState("all"); // all|excel|gtfs|manual — hides services by source
  const [selectedSeg, setSelectedSeg] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [activeDrag, setActiveDrag] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [pdfMenu, setPdfMenu] = useState(false);

  const downloadPdf = async (period, layout) => {
    try {
      localStorage.setItem("logibus_pdf_layout", layout);
      const params = { scope: "day", date_iso: dateIso, period, layout };
      const { data } = await api.get("/planning/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `logibus_planning_${dateIso}_${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setPdfMenu(false);
      toast.success("PDF généré");
    } catch { toast.error("Export PDF impossible"); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchDay = useCallback(async (iso) => {
    setLoading(true);
    try {
      const res = await api.get(`/dispatch/timeline?date_iso=${iso}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await api.get(`/users?role=driver&active_only=true`);
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setDrivers(list);
    } catch (_e) {
      // silent: drivers list is optional
    }
  }, []);

  useEffect(() => { fetchDay(dateIso); }, [dateIso, fetchDay]);
  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  // Realtime: refetch whenever ANY assignment changes anywhere in the app
  // (created/updated/deleted/moved/driver-changed/upserted on tour_assignments).
  // The single broadcast room "dispatch" is fed by every mutation endpoint.
  useRealtime(["dispatch"], {
    "assignment.created":        () => fetchDay(dateIso),
    "assignment.updated":        () => fetchDay(dateIso),
    "assignment.deleted":        () => fetchDay(dateIso),
    "assignment.moved":          () => fetchDay(dateIso),
    "assignment.driver_changed": () => fetchDay(dateIso),
    "assignment.upserted":       () => fetchDay(dateIso),
  });

  // Re-fetch full detail when selecting a segment
  const openDetail = useCallback(async (seg) => {
    setSelectedSeg(seg.id);
    if (seg.type === "adhoc") {
      setSelectedDetail({ ...seg, stops: [], alerts: [] });
      return;
    }
    try {
      const res = await api.get(`/dispatch/planned-services/${seg.id}`);
      setSelectedDetail(res.data);
    } catch (e) {
      toast.error("Détail indisponible");
    }
  }, []);

  const vehicles = data?.vehicles || [];
  const pool = data?.unassigned_pool || [];
  const summary = data?.summary || {};

  // Search/filter
  const filteredVehicles = useMemo(() => {
    let list = vehicles;
    const q = search.trim().toLowerCase();
    // Hide segments whose source doesn't match the active source filter.
    // We expose a 'segments' array shrinked to the filtered ones — vehicles
    // without any remaining segment fall through to the regular filter logic.
    if (sourceFilter !== "all") {
      list = list.map((v) => ({
        ...v,
        segments: (v.segments || []).filter((s) => {
          const src = s.gtfs_trip_id ? "gtfs" : s.template_id ? "excel" : "manual";
          return src === sourceFilter;
        }),
      }));
    }
    if (q) {
      list = list.filter((v) =>
        (v.plate || "").toLowerCase().includes(q) ||
        (v.model || "").toLowerCase().includes(q) ||
        (v.label || "").toLowerCase().includes(q) ||
        (v.driver_name || "").toLowerCase().includes(q) ||
        (v.segments || []).some((s) => (s.code || "").toLowerCase().includes(q) || (s.client_name || "").toLowerCase().includes(q))
      );
    }
    if (filter === "busy")      list = list.filter((v) => (v.segments || []).length > 0);
    else if (filter === "free") list = list.filter((v) => (v.segments || []).length === 0);
    else if (filter === "conflict") list = list.filter((v) => (v.segments || []).some((s) => s.has_conflict));
    else if (filter === "no_gps")  list = list.filter((v) => !v.gps_ok);
    else if (filter === "no_driver") list = list.filter((v) => (v.segments || []).some((s) => !s.driver_id));
    return list;
  }, [vehicles, search, filter, sourceFilter]);

  // ── Drag handlers ──
  const onDragStart = (evt) => {
    setActiveDrag(evt.active?.data?.current || null);
  };
  const onDragEnd = async (evt) => {
    setActiveDrag(null);
    const a = evt.active?.data?.current;
    const overData = evt.over?.data?.current;
    if (!a || !overData || overData.type !== "vehicle") return;
    if (a.fromVehicleId === overData.vehicleId) return; // same lane, no-op
    // Planning Bus is view-only — redirect the user to the source of truth
    // page (/admin/fleet-assignments) with the affected tour pre-focused.
    if (a.tour_id) {
      toast.info("Planning Bus est en lecture seule — redirection vers Affectations flotte");
      window.location.href = `/admin/fleet-assignments?focus_tour=${a.tour_id}&kind=durable`;
    } else {
      toast.warning("Les modifications doivent être faites dans Affectations flotte");
    }
  };

  const onChangeDriver = async (_driverId) => {
    // Planning Bus = read-only — no longer used (kept as no-op for legacy
    // callsites; the detail panel now uses a direct link to /admin/vehicles).
  };

  const onUnassign = async () => {
    // Planning Bus = read-only — no longer used.
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="min-h-[calc(100vh-3rem)] bg-zinc-100" data-testid="admin-dispatch-page">
        {/* Header */}
        <div className="bg-zinc-900 text-white px-6 py-4 border-b-2 border-[#FFC60A]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <Sparkles size={22} className="text-[#FFC60A]" />
                <h1 className="font-display font-black text-2xl tracking-tight leading-none">Planning Bus</h1>
                <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400 mt-1">Dispatch · {user?.tenant?.name || "Admin"}</span>
              </div>
              <div className="text-zinc-300 mt-1 text-sm">{frenchDow(dateIso)}</div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setDateIso(addDaysIso(dateIso, -1))}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
                data-testid="dispatch-prev-day"
              ><ChevronLeft size={16} /></button>
              <div className="relative">
                <input
                  type="date"
                  value={dateIso}
                  onChange={(e) => setDateIso(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm font-mono"
                  data-testid="dispatch-date-picker"
                />
              </div>
              <div className="relative">
                <button onClick={() => setPdfMenu(!pdfMenu)} data-testid="dispatch-pdf-btn"
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest">
                  <FileDown size={14} /> PDF
                </button>
                {pdfMenu && (
                  <div className="absolute z-[1200] mt-1 left-0 bg-zinc-900 border border-zinc-700 w-60 shadow-xl text-white" data-testid="dispatch-pdf-menu">
                    <button onClick={() => downloadPdf("day", "condensed")} data-testid="dispatch-pdf-day"
                      className="block w-full text-left px-3 py-2 text-xs hover:bg-zinc-800">Jour affiché — 1 page A4</button>
                    <button onClick={() => downloadPdf("week", "condensed")} data-testid="dispatch-pdf-week-condensed"
                      className="block w-full text-left px-3 py-2 text-xs hover:bg-zinc-800">Semaine condensée (affichage dépôt)</button>
                    <button onClick={() => downloadPdf("week", "per_day")} data-testid="dispatch-pdf-week-perday"
                      className="block w-full text-left px-3 py-2 text-xs hover:bg-zinc-800">Semaine détaillée (1 page/jour, arrêts)</button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setDateIso(addDaysIso(dateIso, 1))}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
                data-testid="dispatch-next-day"
              ><ChevronRight size={16} /></button>
              <button
                onClick={() => setDateIso(todayIso())}
                className="px-3 py-2 bg-[#FFC60A] text-zinc-900 font-bold text-xs uppercase tracking-wider"
                data-testid="dispatch-today"
              ><CalendarIcon size={13} className="inline mr-1" />Aujourd&apos;hui</button>
              <a
                href="/admin/fleet-assignments"
                data-testid="dispatch-edit-redirect"
                className="px-3 py-2 bg-zinc-900 text-[#FFC60A] border border-[#FFC60A] font-bold text-xs uppercase tracking-wider hover:bg-[#FFC60A] hover:text-zinc-900 inline-flex items-center gap-1.5">
                <Sparkles size={12} /> Modifier dans Affectations flotte ↗
              </a>
              <button
                onClick={() => fetchDay(dateIso)}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
                data-testid="dispatch-refresh"
                disabled={loading}
              >{loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}</button>
            </div>
          </div>

          {/* KPIs */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Kpi label="Bus" value={summary.bus_total ?? 0} testId="dispatch-kpi-bus" />
            <Kpi label="Affectés" value={summary.assigned ?? 0} tone="ok" testId="dispatch-kpi-assigned" />
            <Kpi label="Libres" value={summary.free ?? 0} testId="dispatch-kpi-free" />
            <Kpi label="Sans chauffeur" value={summary.no_driver ?? 0} tone="warn" testId="dispatch-kpi-no-driver" />
            <Kpi label="GPS absent" value={summary.no_gps ?? 0} tone="warn" testId="dispatch-kpi-no-gps" />
            <Kpi label="Conflits" value={summary.conflicts ?? 0} tone="alert" testId="dispatch-kpi-conflicts" />
            <Kpi label="En attente" value={summary.unassigned_pool ?? 0} tone="pool" testId="dispatch-kpi-pool" />
            {(summary.orphaned_assignments ?? 0) > 0 && (
              <button
                onClick={async () => {
                  if (orphans) { setOrphans(null); return; }
                  try {
                    const { data: d } = await api.get("/tour-assignments", { params: { status: "orphaned_by_reimport" } });
                    setOrphans(d.items || []);
                  } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
                }}
                className="px-3 py-1.5 bg-amber-500/20 border border-amber-500 text-amber-300 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-2 hover:bg-amber-500/30 transition-colors"
                data-testid="dispatch-orphaned-badge"
              >
                <AlertTriangle size={12} /> {summary.orphaned_assignments} à réaffecter (réimport)
              </button>
            )}
          </div>

          {summary.import_in_progress && (
            <div className="mt-3 bg-sky-500/15 border border-sky-500 px-3 py-2 text-[11px] text-sky-300 font-mono uppercase tracking-widest inline-flex items-center gap-2" data-testid="dispatch-import-in-progress">
              <Loader2 size={12} className="animate-spin" /> Mise à jour du programme en cours — le planning se rafraîchira automatiquement
            </div>
          )}

          {orphans && (
            <div className="mt-3 bg-zinc-800 border border-amber-500/50 p-3" data-testid="dispatch-orphaned-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">
                  Affectations orphelines après réimport — la tournée d&apos;origine n&apos;existe plus, réaffectez le bus
                </div>
                <a href="/admin/fleet-assignments" className="text-[10px] font-black uppercase tracking-widest text-white underline hover:text-[#FFC60A]" data-testid="dispatch-orphaned-goto">
                  Affectations flotte →
                </a>
              </div>
              <div className="divide-y divide-zinc-700 max-h-40 overflow-y-auto">
                {orphans.map((o) => (
                  <div key={o.id} className="flex items-center gap-4 py-1.5 text-[11px] text-zinc-300" data-testid={`dispatch-orphan-${o.id}`}>
                    <span className="font-mono font-bold text-amber-300">{o.orphaned_service_code || o.tour_label || "?"}</span>
                    <span>Bus : {o.vehicle_label || "—"}</span>
                    <span>Chauffeur : {o.driver_name || "—"}</span>
                    {o.orphaned_at && <span className="text-zinc-500">depuis {String(o.orphaned_at).slice(0, 10)}</span>}
                  </div>
                ))}
                {orphans.length === 0 && <div className="text-[11px] text-zinc-500 py-1.5">Aucune</div>}
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute top-1/2 -translate-y-1/2 left-2 text-zinc-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher bus, service ou client..."
                className="pl-7 pr-3 py-1.5 text-sm bg-zinc-800 border border-zinc-700 text-white w-72"
                data-testid="dispatch-search"
              />
            </div>
            {[
              { v: "all",       l: "Tous" },
              { v: "busy",      l: "Occupés" },
              { v: "free",      l: "Libres" },
              { v: "conflict",  l: "Conflits" },
              { v: "no_gps",    l: "Sans GPS" },
              { v: "no_driver", l: "Sans chauffeur" },
            ].map((b) => (
              <button
                key={b.v}
                onClick={() => setFilter(b.v)}
                className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider border ${filter === b.v ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700"}`}
                data-testid={`dispatch-filter-${b.v}`}
              >{b.l}</button>
            ))}
            {/* Source filter — separated by a divider */}
            <div className="w-px h-6 bg-zinc-700 mx-1" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">Source:</span>
            {[
              { v: "all",    l: "Toutes" },
              { v: "excel",  l: "Excel" },
              { v: "gtfs",   l: "GTFS" },
              { v: "manual", l: "Manuel" },
            ].map((b) => (
              <button
                key={`src-${b.v}`}
                onClick={() => setSourceFilter(b.v)}
                className={`px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider border ${sourceFilter === b.v ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700"}`}
                data-testid={`dispatch-source-${b.v}`}
              >{b.l}</button>
            ))}
          </div>
        </div>

        {/* Read-only banner */}
        <div data-testid="dispatch-readonly-banner"
              className="bg-sky-50 border-y border-sky-300 px-6 py-2 flex items-center gap-3 text-sm">
          <Layers size={14} className="text-sky-700 shrink-0" />
          <div className="flex-1">
            <strong className="font-display font-black tracking-tight">Visualisation uniquement</strong>
            <span className="text-zinc-700">
              {" "}— les modifications d&apos;affectation se font dans
              <a href="/admin/fleet-assignments" className="underline font-bold ml-1 hover:text-sky-700">
                Affectations flotte
              </a>. Cette page affiche automatiquement le résultat.
            </span>
          </div>
        </div>

        {/* Empty-day banner — explains WHY the planning is empty */}
        {!loading && data && (summary.unassigned_pool ?? 0) === 0 &&
          vehicles.every((v) => !(v.segments || []).length) && (
          <div data-testid="dispatch-empty-day-banner"
                className="bg-amber-50 border-y border-amber-300 px-6 py-3 flex items-center gap-3 text-sm">
            <CalendarIcon size={16} className="text-amber-700 shrink-0" />
            <div className="flex-1">
              <strong className="font-display font-black tracking-tight">Aucune course planifiée le {frenchDow(dateIso)}</strong>
              <span className="text-zinc-700">
                {" "}— jour sans école (week-end, vacances) ou aucune tournée importée.
                {data.next_active_date && (
                  <> Prochaine journée de circulation :{" "}
                    <button onClick={() => setDateIso(data.next_active_date)}
                      data-testid="dispatch-goto-next-active"
                      className="underline font-bold hover:text-amber-700">
                      {frenchDow(data.next_active_date)}
                    </button>
                  </>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex" style={{ height: "calc(100vh - 280px)" }}>
          {/* Left fixed sidebar */}
          <div className="flex bg-white border-r border-zinc-300 overflow-hidden">
            <div className="flex flex-col" style={{ width: SIDEBAR_W }}>
              <div className="h-10 bg-zinc-900 text-white flex items-center px-3 text-[10px] uppercase tracking-[0.2em] font-mono border-b-2 border-[#FFC60A]">
                Bus ({filteredVehicles.length}/{vehicles.length})
              </div>
              <div className="overflow-y-auto flex-1" id="dispatch-sidebar-scroll" data-testid="dispatch-sidebar">
                {filteredVehicles.map((v) => (<VehicleCell key={v.id} vehicle={v} />))}
                {filteredVehicles.length === 0 && (
                  <div className="text-xs text-zinc-500 p-4 text-center">Aucun bus correspondant</div>
                )}
              </div>
            </div>
          </div>

          {/* Scrollable timeline */}
          <div className="flex-1 overflow-auto bg-zinc-50 relative" data-testid="dispatch-timeline-scroll">
            <div style={{ width: totalWidth }}>
              <TimeRuler />
              {filteredVehicles.map((v) => (
                <VehicleLane
                  key={v.id}
                  vehicle={v}
                  onSelect={openDetail}
                  selectedSegId={selectedSeg}
                />
              ))}
              {filteredVehicles.length === 0 && !loading && (
                <div className="text-center text-zinc-500 p-10 text-sm">Aucune donnée</div>
              )}
              {loading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                  <Loader2 size={32} className="animate-spin text-[#FFC60A]" />
                </div>
              )}
            </div>
          </div>

          {/* Unassigned pool */}
          <div className="bg-white border-l-2 border-zinc-300 flex flex-col" style={{ width: POOL_W }}>
            <div className="h-10 bg-zinc-900 text-[#FFC60A] flex items-center px-3 text-[10px] uppercase tracking-[0.2em] font-mono border-b-2 border-[#FFC60A]">
              <Layers size={12} className="mr-1.5" /> En attente ({pool.length})
            </div>
            <div className="overflow-y-auto flex-1 p-2 space-y-2" data-testid="dispatch-pool">
              {pool.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 text-center">Tous les services sont affectés</div>
              ) : pool.map((p) => (
                <PoolItem key={p.id} seg={p} onSelect={openDetail} selected={selectedSeg === p.id} />
              ))}
            </div>
          </div>
        </div>

        {/* Right detail panel */}
        {selectedDetail && (
          <ServiceDetailPanel
            ps={selectedDetail}
            onClose={() => { setSelectedDetail(null); setSelectedSeg(null); }}
          />
        )}

        <DragOverlay>
          {activeDrag && (
            <div className="bg-[#FFC60A] text-zinc-900 font-bold text-xs px-3 py-2 shadow-2xl border-2 border-zinc-900 rounded">
              {activeDrag.code || "service"}
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
}
